(() => {
  const instances = new Map();

  function mount(root) {
    if (instances.has(root)) return;
    const controller = new AbortController();
    const { signal } = controller;
    const cleanups = [];
    const on = (element, event, listener, options = {}) => element?.addEventListener(event, listener, { ...options, signal });
    const find = selector => root.querySelector(selector);
    const all = selector => [...root.querySelectorAll(selector)];

    // The theme owns analytics and consent. Optional listeners can consume these
    // events without this section injecting a second tracking SDK or property.
    const track = (name, detail = {}) => root.dispatchEvent(new CustomEvent('repod:interaction', {
      bubbles: true, detail: { name, language: root.lang, ...detail },
    }));

    const hiddenChrome = [];
    if (root.dataset.hideThemeChrome === 'true' && root.dataset.themeChrome) {
      try {
        document.querySelectorAll(root.dataset.themeChrome).forEach(element => {
          if (!element.contains(root) && !root.contains(element) && !element.classList.contains('repod-theme-hidden')) {
            element.classList.add('repod-theme-hidden');
            hiddenChrome.push(element);
          }
        });
      } catch { /* An invalid optional selector must not break page interactions. */ }
    }
    cleanups.push(() => hiddenChrome.forEach(element => element.classList.remove('repod-theme-hidden')));

    const menu = find('[data-repod-menu]');
    const menuTrigger = find('.menu-trigger');
    let bodyOverflow;
    let menuWasOpen = false;
    function closeMenu() {
      if (menu.open) menu.close();
    }
    on(menuTrigger, 'click', () => {
      bodyOverflow = document.body.style.overflow;
      menuWasOpen = true;
      menu.showModal();
      document.body.style.overflow = 'hidden';
      menuTrigger.setAttribute('aria-expanded', 'true');
      menu.querySelector('button').focus();
    });
    on(menu, 'close', () => {
      if (menuWasOpen) document.body.style.overflow = bodyOverflow;
      menuWasOpen = false;
      menuTrigger.setAttribute('aria-expanded', 'false');
      menuTrigger.focus({ preventScroll: true });
    });
    on(menu, 'click', event => {
      if (event.target.closest('a,[data-close-menu]') || event.target === menu) closeMenu();
    });
    cleanups.push(() => {
      if (menuWasOpen) document.body.style.overflow = bodyOverflow;
      closeMenu();
    });

    const picker = find('.language-picker');
    on(document, 'pointerdown', event => { if (picker && !picker.contains(event.target)) picker.open = false; });
    on(picker, 'keydown', event => {
      if (event.key === 'Escape') { picker.open = false; picker.querySelector('summary').focus(); }
    });

    const gallery = find('.gallery');
    const slides = all('.gallery-slide');
    const indicators = all('.gallery-indicators button');
    let index = 0;
    let touchStart = null;
    function select(position) {
      index = (position + slides.length) % slides.length;
      find('.gallery-track').style.transform = `translateX(-${index * 100}%)`;
      slides.forEach((slide, i) => slide.setAttribute('aria-hidden', String(i !== index)));
      indicators.forEach((button, i) => {
        button.classList.toggle('active', i === index);
        button.setAttribute('aria-pressed', String(i === index));
      });
      find('.gallery-caption').textContent = slides[index].dataset.caption;
      track('gallery_view', { view: slides[index].dataset.view });
    }
    on(find('.gallery-arrow.previous'), 'click', () => select(index - 1));
    on(find('.gallery-arrow.next'), 'click', () => select(index + 1));
    indicators.forEach((button, i) => on(button, 'click', () => select(i)));
    on(gallery, 'keydown', event => {
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        select(index + (event.key === 'ArrowRight' ? 1 : -1));
      }
    });
    on(gallery, 'touchstart', event => { touchStart = event.changedTouches[0].clientX; }, { passive: true });
    on(gallery, 'touchend', event => {
      if (touchStart === null) return;
      const delta = event.changedTouches[0].clientX - touchStart;
      if (Math.abs(delta) > 45) select(index + (delta < 0 ? 1 : -1));
      touchStart = null;
    }, { passive: true });
    on(gallery, 'touchcancel', () => { touchStart = null; });

    const detailDialog = find('.detail-dialog');
    let detailTrigger;
    all('.detail-item').forEach(button => on(button, 'click', () => {
      const source = button.querySelector('img');
      const image = detailDialog.querySelector('img');
      image.src = source.src;
      image.srcset = source.srcset;
      image.sizes = '(max-width: 1100px) 94vw, 1000px';
      image.alt = source.alt;
      detailDialog.querySelector('[data-detail-title]').textContent = button.dataset.detailTitle;
      detailDialog.querySelector('[data-detail-copy]').textContent = button.dataset.detailCopy;
      detailTrigger = button;
      detailDialog.showModal();
      track('detail_open', { title: button.dataset.detailTitle });
    }));
    on(detailDialog.querySelector('.close-button'), 'click', () => detailDialog.close());
    on(detailDialog, 'click', event => { if (event.target === detailDialog) detailDialog.close(); });
    on(detailDialog, 'close', () => detailTrigger?.focus({ preventScroll: true }));
    cleanups.push(() => { if (detailDialog.open) detailDialog.close(); });

    const film = find('.film');
    const preview = find('[data-film-preview]');
    const full = find('[data-film-full]');
    const filmButton = find('.watch-film');
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let filmInView = false;
    let expanded = false;
    function syncFilm() {
      if (!filmInView || document.visibilityState !== 'visible') {
        preview.pause(); full.pause();
      } else if (expanded || motion.matches) preview.pause();
      else { preview.muted = true; preview.play().catch(() => {}); }
    }
    on(filmButton, 'click', () => {
      expanded = !expanded;
      film.classList.toggle('expanded', expanded);
      filmButton.setAttribute('aria-expanded', String(expanded));
      filmButton.querySelector('[data-film-label]').textContent = expanded ? root.dataset.closeFilm : root.dataset.watchFilm;
      preview.hidden = expanded;
      full.hidden = !expanded;
      full.controls = expanded;
      full.tabIndex = expanded ? 0 : -1;
      if (expanded) {
        preview.pause(); full.currentTime = 0; full.muted = false;
        full.play().catch(() => full.focus());
      } else { full.pause(); full.muted = true; syncFilm(); }
    });
    on(motion, 'change', syncFilm);
    on(document, 'visibilitychange', syncFilm);

    const wheel = find('.wheel-sound video');
    const wheelButton = find('.wheel-listen');
    const wheelError = find('[data-wheel-error]');
    function wheelState() {
      wheelButton.querySelector('[data-wheel-label]').textContent = wheel.paused ? root.dataset.listenWheel : root.dataset.pauseWheel;
      wheelButton.querySelector('[aria-hidden]').textContent = wheel.paused ? '▷' : 'Ⅱ';
    }
    on(wheelButton, 'click', () => {
      if (!wheel.paused) { wheel.pause(); return; }
      if (wheel.ended) wheel.currentTime = 0;
      wheel.muted = false;
      wheel.controls = true;
      wheelError.hidden = true;
      wheel.play().catch(() => { wheelError.hidden = false; });
    });
    on(wheel, 'playing', () => { wheelState(); track('video_start', { video: 'wheel' }); });
    on(wheel, 'pause', wheelState);
    on(wheel, 'ended', wheelState);
    on(wheel, 'error', () => { wheelError.hidden = false; wheelState(); });
    on(document, 'visibilitychange', () => { if (document.visibilityState !== 'visible') wheel.pause(); });

    if ('IntersectionObserver' in window) {
      const filmObserver = new IntersectionObserver(([entry]) => {
        filmInView = entry.isIntersecting && entry.intersectionRatio >= 0.25;
        syncFilm();
      }, { threshold: [0, 0.25] });
      filmObserver.observe(film);
      const wheelObserver = new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.25) wheel.pause();
      }, { threshold: [0, 0.25] });
      wheelObserver.observe(wheel);
      cleanups.push(() => { filmObserver.disconnect(); wheelObserver.disconnect(); });
    }
    all('.question-list details').forEach(item => on(item, 'toggle', () => {
      if (item.open) track('faq_open', { question: item.id });
    }));
    on(root, 'click', event => {
      const link = event.target.closest('[data-store-link]');
      if (link) track('store_click', { placement: link.dataset.storeLink });
    });
    cleanups.push(() => { preview.pause(); full.pause(); wheel.pause(); });

    instances.set(root, () => {
      cleanups.forEach(cleanup => cleanup());
      controller.abort();
      instances.delete(root);
    });
  }

  function scan(scope = document) {
    if (scope.matches?.('[data-repod-page]')) mount(scope);
    scope.querySelectorAll('[data-repod-page]').forEach(mount);
  }
  if (!window.RepodShopify) {
    window.RepodShopify = { scan };
    document.addEventListener('shopify:section:load', event => scan(event.target));
    document.addEventListener('shopify:section:unload', event => {
      for (const [root, cleanup] of instances) if (event.target.contains(root)) cleanup();
    });
  }
  window.RepodShopify.scan();
})();
